import { useCallback, useEffect, useState } from "react";
import { resolveSkin } from "./lib/skin.js";
import { SKINS } from "./skins/index.js";

/**
 * Two views, one transition. The session id lives in the URL so a reload
 * mid-build resumes the same session rather than starting over — the stream
 * replays from the server's durable log.
 *
 * Which pair of views those are is the skin's business and this component's
 * only other decision. Routing is identical across skins, which is why it lives
 * here rather than being reimplemented inside each one.
 */
export function App() {
  const [sessionId, setSessionId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("session"),
  );

  // Resolved once, from the URL this page was opened with. Re-reading it per
  // render would let a rewrite below swap the skin mid-session.
  const [{ PromptView, BuildView }] = useState(() => SKINS[resolveSkin()]);

  // Back/forward should move between the prompt and a build, not strand the
  // user on a view the URL no longer describes.
  useEffect(() => {
    const onPopState = () => {
      setSessionId(new URLSearchParams(window.location.search).get("session"));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const start = useCallback((id: string) => {
    window.history.pushState({}, "", urlWith({ session: id }));
    setSessionId(id);
  }, []);

  const restart = useCallback(() => {
    window.history.pushState({}, "", urlWith({ session: null }));
    setSessionId(null);
  }, []);

  return sessionId ? (
    <BuildView sessionId={sessionId} onRestart={restart} />
  ) : (
    <PromptView onStarted={start} />
  );
}

/**
 * The current URL with `session` set or cleared, and everything else kept.
 *
 * Written this way rather than as a bare `?session=…` because `?ui=` has to
 * survive the rewrite. Without that, opening `?ui=hatch`, starting a build, and
 * reloading mid-build lands on the deployed default — which defeats the whole
 * point of an override meant for comparing skins against one live droplet.
 */
function urlWith({ session }: { session: string | null }): string {
  const params = new URLSearchParams(window.location.search);
  if (session === null) params.delete("session");
  else params.set("session", session);

  const query = params.toString();
  return query ? `${window.location.pathname}?${query}` : window.location.pathname;
}
